import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { Translation } from 'primeng/api';

/** Języki UI admina — odpowiadają plikom src/assets/i18n/{lang}.json. */
export type AppLanguage = 'pl' | 'en';
export const APP_LANGUAGES: readonly AppLanguage[] = ['pl', 'en'];
/** Decyzja 2026-10-16: PL domyślnie wszędzie (panel, backend, admin). */
export const DEFAULT_APP_LANGUAGE: AppLanguage = 'pl';

const PRIMENG_LOCALE_PL: Translation = {
  firstDayOfWeek: 1,
  dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
  dayNamesShort: ['Nie', 'Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob'],
  dayNamesMin: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
  monthNames: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
  monthNamesShort: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
  today: 'Dziś',
  clear: 'Wyczyść',
  weekHeader: 'Tydz',
  accept: 'Tak',
  reject: 'Nie',
  emptyMessage: 'Brak wyników',
  emptyFilterMessage: 'Brak wyników',
};

const PRIMENG_LOCALE_EN: Translation = {
  firstDayOfWeek: 1,
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  today: 'Today',
  clear: 'Clear',
  weekHeader: 'Wk',
  accept: 'Yes',
  reject: 'No',
  emptyMessage: 'No results found',
  emptyFilterMessage: 'No results found',
};

const PRIMENG_LOCALES: Record<AppLanguage, Translation> = {
  pl: PRIMENG_LOCALE_PL,
  en: PRIMENG_LOCALE_EN,
};

/**
 * Język UI admina: zapamiętany wybór (localStorage `userLanguage`) albo domyślnie `pl`.
 * Języka przeglądarki NIE wykrywamy (angielski system dawał angielski admin). Ustawia aktywny
 * język Transloco, tłumaczenia PrimeNG i `<html lang>` (czytniki ekranu, dzielenie wyrazów).
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly LANGUAGE_KEY = 'userLanguage';
  private readonly transloco = inject(TranslocoService);
  private readonly primeng = inject(PrimeNG);
  private readonly document = inject(DOCUMENT);
  public readonly language: WritableSignal<AppLanguage>;

  constructor() {
    const stored = this.getStoredLanguage();
    const lang = stored !== null && this.isSupported(stored) ? stored : DEFAULT_APP_LANGUAGE;
    this.language = signal(lang);
    this.apply(lang);
  }

  public setLanguage(lang: string): void {
    if (!this.isSupported(lang)) {
      return;
    }
    this.language.set(lang);
    this.storeLanguage(lang);
    this.apply(lang);
  }

  public getCurrentLanguage(): AppLanguage {
    return this.language();
  }

  private apply(lang: AppLanguage): void {
    this.transloco.setActiveLang(lang);
    this.primeng.setTranslation(PRIMENG_LOCALES[lang]);
    this.document.documentElement.lang = lang;
  }

  private isSupported(lang: string): lang is AppLanguage {
    return (APP_LANGUAGES as readonly string[]).includes(lang);
  }

  /** localStorage bywa niedostępny (tryb prywatny, zablokowane dane witryny) — wtedy domyślny język. */
  private getStoredLanguage(): string | null {
    try {
      return localStorage.getItem(this.LANGUAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeLanguage(lang: AppLanguage): void {
    try {
      localStorage.setItem(this.LANGUAGE_KEY, lang);
    } catch {
      // Brak trwałego zapisu nie blokuje zmiany języka w bieżącej sesji.
    }
  }
}
